import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountsApi, type Account } from '@/api/accounts';
import { transactionsApi, type OwnerRegisterLine } from '@/api/transactions';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { AllocateDialog } from '@/features/accounts/AllocateDialog';
import { OwnedEnvelopesProvider } from '@/features/accounts/OwnedEnvelopesProvider';

interface SessionAllocateDialogProps {
  onClose: () => void;
  onNotify: (message: string) => void;
}

export function SessionAllocateDialog({ onClose, onNotify }: SessionAllocateDialogProps) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState<number | ''>('');
  const [lines, setLines] = useState<OwnerRegisterLine[]>([]);
  const [selected, setSelected] = useState<OwnerRegisterLine | null>(null);
  const account = accounts.find((row) => row.id === accountId) ?? null;
  const started = useRef(false);

  // The fetch-once effect below survives StrictMode's re-mount on its ref guard, so its result is
  // gated on this mounted ref, not on a per-run flag (a cancelled first run would drop it).
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void accountsApi
      .list()
      .then((list) => {
        if (mounted.current) setAccounts(list);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (accountId === '') return;
    let cancelled = false;
    void transactionsApi
      .register(accountId, { unallocatedOnly: true })
      .then((page) => {
        if (!cancelled) setLines(page.items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accountId]);
  const visibleLines = accountId === '' ? [] : lines;

  // This dialog is a picker (an account, then a line) in front of the allocation editor, the
  // `AllocateDialog` it opens, which guards the allocations themselves. Picking is not unsaved
  // work (one click to redo, like Move and Merge), so nothing here asks the discard question
  // (TMMAPP-0082: the Architect reversed 0081's ruling).
  return (
    <>
      <Dialog
        open={selected === null}
        onClose={onClose}
        aria-labelledby="alloc-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="alloc-title">{t('meetings.allocateTitle')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="alloc-account">{t('meetings.allocatePickAccount')}</InputLabel>
            <Select
              labelId="alloc-account"
              label={t('meetings.allocatePickAccount')}
              value={accountId === '' ? '' : String(accountId)}
              onChange={(event) =>
                setAccountId(event.target.value === '' ? '' : Number(event.target.value))
              }
            >
              {accounts.map((row) => (
                <MenuItem key={row.id} value={String(row.id)}>
                  {row.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {accountId !== '' && visibleLines.length === 0 && (
            <Typography sx={{ mt: 2 }}>{t('meetings.allocateEmpty')}</Typography>
          )}
          {visibleLines.map((line) => (
            <Button
              key={line.id}
              fullWidth
              sx={{ justifyContent: 'flex-start', mt: 1 }}
              onClick={() => setSelected(line)}
            >
              {`${line.payee} · ${line.postedOn}`}
            </Button>
          ))}
        </DialogContent>
        <DialogActions>
          <DialogDoneButton onClick={onClose} />
        </DialogActions>
      </Dialog>
      {selected !== null && account !== null && (
        <OwnedEnvelopesProvider>
          <AllocateDialog
            account={account}
            line={selected}
            editor
            owner
            onClose={() => {
              setSelected(null);
              onClose();
            }}
            onChanged={() => {
              setSelected(null);
              onClose();
            }}
            onNotify={onNotify}
          />
        </OwnedEnvelopesProvider>
      )}
    </>
  );
}
